<template>
    <div class="row mb-2">
        <div class="col-12 col-sm-5 col-md-5 col-lg-4">
            <div class="patient-action">
                <span> Select Patient </span>
                <div class="col-lg-4">
                    <input
                        v-model="patientSerialNo"
                        name="serial"
                        type="number"
                        class="form-control"
                        placeholder="Patient Id"
                        @keyup="searchPatientById"
                    />
                </div>
            </div>
            <div class="col-md-7 col-lg-12">
                <AsyncSelect
                    placeholder="Patient Name, ID, Mobile No"
                    v-model="patient"
                    :api-service="fetchContactProfiles"
                    :additional-query="additionalQuery"
                    :format-label="formatPatientLabel"
                />
            </div>
        </div>

        <div class="col-12 col-sm-6 col-md-6 col-lg-1 mt-2">
            <span> Patient ID </span>
            <input
                :value="patientId"
                type="text"
                class="form-control"
                readonly
            />
        </div>

        <div class="col-lg-5"></div>

        <div class="col-12 col-sm-6 col-md-6 col-lg-2 mt-2">
            <div class="col-md-5 d-flex align-items-center">
                    <span> Invoice Date </span>      
            </div>
            <div class="col-md-7 col-lg-12">
                <input
                    class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                    tabindex="1"
                    type="date"
                    placeholder=""
                    v-model="formData.date"
                >
            </div>
        </div>
    </div>
    
    <div class="mb-2">
        <AddPatient
            :doctors="doctors"
            :genders="genders"
            @onCreateProfile="onCreateProfile"
        />
        <hr>
    </div>

    <div class="row mb-2">
        <div class="col-12 col-sm-5 col-md-12 col-lg-3">
            <div class="d-flex justify-content-between">
                <div class="mt-auto">
                    <span> Consultant / Doctor </span>                
                </div>
                <div class="btn btn-outline-primary btn-sm" @click="addNewConsultant">
                    Add New
                </div>
            </div>
            <div class="col-md-6 col-lg-12">
                <AsyncSelect
                    placeholder="Select consultant"
                    v-model="formData.service_resource_id"
                    :api-service="fetchServiceList"
                    :additional-query="{ resource_type: 'human_resources' }"
                    :format-label="formatPatientLabel"
                    :reduce="consultant => consultant.id"
                    :additional-option="consultantAdditionalOption"
                />
            </div>
        </div>

        <div class="col-12 col-sm-6 col-md-6 col-lg-3 mt-8">
            <div class="col-md-2 d-flex align-items-center col-lg-12">
                <span> Out Doctor Name </span>
            </div>
            <div class="col-md-6 col-lg-12">
                <input
                    class="form-control invoice-edit-input"
                    tabindex="1"
                    type="text"
                    placeholder="Out Doctor Name "
                    v-model="formData.transport_reference"
                >
            </div>
        </div>

        <div class="col-12 col-sm-6 col-md-6 col-lg-3 mt-8">
          <div class="col-md-5 d-flex align-items-center">
            <span> Select Client </span>
          </div>
          <div class="col-md-7 col-lg-12">
            <AsyncSelect
                placeholder="Select Client"
                v-model="formData.contact_profile_client_id"
                :api-service="fetchContactProfiles"
                :additional-query="{ type: 'client' }"
                :format-label="formatPatientLabel"
                :reduce="referrer => referrer.id"
                :additional-option="clientAdditionalOption"
            />
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-6 col-lg-3">
            <div class="d-flex justify-content-between">
                <div class="mt-auto">
                    <span> Select Referrer </span>                 
                </div>
                <div class="btn btn-outline-primary btn-sm" @click="addNewContact">
                    Add New
                </div>
            </div>
            <div class="col-md-7 col-lg-12">
                <AsyncSelect
                    placeholder="Select Referrer"
                    v-model="formData.referrer_id"
                    :api-service="fetchContactProfiles"
                    :additional-query="{ type: 'agent' }"
                    :format-label="formatPatientLabel"
                    :additional-option="referrerAdditionalOption"
                    :reduce="referrer => referrer.id"
                />
            </div>
        </div>

    </div>

    <div v-if="! $route.params.invoiceId" class="row py-2">
        <div class="col-12 col-sm-5 col-md-12 col-lg-12">
            <div class="col-md-5 col-lg-12">
                <AsyncSelect
                    ref="productSelect"
                    placeholder="Select Service"
                    v-model="productId"
                    :api-service="fetchProductBySlug"
                    :reduce="name => name.id"
                    :format-label="option => option.text"
                    :additional-query="additionalProductQuery"
                    label="text"
                    input-class="product-field"
                />
            </div>
        </div>
    </div>

    <ServiceResourceAddModal
        :showParent="true"
        :modalTitle="'Add Consultant / Doctor'"
        ref="resourceModalRef"
        :type="resourceType"
        :formData="resourceFormData"
        :specifications="resourceSpecifications"
        :products="productList"
        :userList="userList"
        :accountHeads="accountHeads"
        :group="serviceResourceGroups"
        @onSubmit="handleResourceSubmit"
    />

    <AddContactBasic
        v-if="$store.state.isModalOpenOne"
        modalStateKey="isModalOpenOne"
        contactType="organization"
        :role="'agent'"
        @onCreateProfile="onCreateContact"
    />
    <Loader v-if="isLoader" />
</template>

<script setup>
import { ref, onMounted, watch, inject, computed, reactive } from 'vue'
import { useRoute } from 'vue-router'
import {useStore} from "vuex";
import handlePurchase from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'
import handleHospitalBilling from "@/services/modules/hospital/billing";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleInventory from "@/services/modules/inventory";
import handleHospital from '@/services/modules/hospital'
import AddPatient from '@/components/molecule/company/hospital/billing/investigation/dc/AddPatient.vue'
import useDate from "@/services/utils/day";
import Loader from "@/components/atom/LoaderComponent";
import AddContactBasic from '@/components/atom/Contact/AddContactBasic.vue';
import ServiceResourceAddModal from '@/components/molecule/company/hospital/service-resources/ServiceResourceAddModal';
import handlePurchaseAndSales from "@/services/modules/purchase";
import handleImportTracker from "@/services/modules/procurement/importTracker";

const props = defineProps({
  addNewItem: {
    type: Function
  },
  products: {
    type: Array,
    default: []
  },
  tableItems: {
    type: Array
  },
  resourceType: {
    type: Object
  },
  resourceSpecifications: {
    type: Object
  },
  genders: {
    type: Object,
  },
  accountHeads: {
    type: Array
  },
  serviceResourceRes: {
    type: Object
  },
  referrerRes: {
    type: Object
  },
  clientRes: {
    type: Object
  },
})

const $route = useRoute();
const $store = useStore();
const date = useDate();
const patient = ref(null);
const userInfo = ref(null);
const isLoader = ref(false);
const patientSerialNo = ref(null);
const companyId = $route.params.companyId
const companyQuery = `?company_id=${companyId}`;
const selectedData = ref({});
const formData = inject('formData');
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const productId = ref(null);
const productSelect = ref(null);
const registrationList = ref([]);
const additionalQuery = {
    type: "patient"
}
const additionalProductQuery = {
    product_type: "services",
    slug: "investigation",
    with_rate: true
}
const doctors = reactive([]);
const referrerAdditionalOption = ref([]);
const clientAdditionalOption = ref([]);

const { fetchServiceList, fetchPatientDetailsBySerial, storeServiceResource, fetchGroup } = handleHospital()
const {fetchProductBySlug} = handlePurchase()
const { getProduct } = handleInventory()
const {fetchContactProfiles, fetchSingleContactProfile} = handleContact()
const { fetchIpdRegisterList } = handleHospitalBilling()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchProductList } = handlePurchaseAndSales();
const { fetchContacts } = handleImportTracker();

const loading = ref(true);
const productList = ref([]);
const userList = ref([]);
const serviceResourceGroups = ref([]);
const consultantAdditionalOption = ref([]);
const resourceModalRef = ref(null);
const resourceFormData = ref({
    contact_profile_id: '',
    service_user_id: null,
    name: '',
    service_code: '',
    short_description: '',
    full_description: '',
    resource_type: 'human_resources',
    top_margin: 170,
    bottom_margin: 86,
    left_margin: 25,
    right_margin: 25,
    status: 'active',
    company_id: '',
    head_group_id: null,
    resource_descriptions: [
        {
          product_id: '',
          service_resource_id: '',
          sales_account_head_id: '',
          sales_price: 0,
          cost_of_sales_account_head_id: '',
          cost_of_sales_price: 0,
          service_specification: '',
          service_duration: '',
          is_opd_billing: false,
          is_repeat_weekly: 1,
          schedules: '',
        }
    ]
})

//computed
const patientId = computed(() => {
    if (
        selectedData.value.company_roles &&
        selectedData.value.company_roles.length &&
        selectedData.value.company_roles[0].pivot
    ) {
        return selectedData.value.company_roles[0].pivot.serial_no
    }
    return null
})

onMounted(async () => {
    formData.value.date = date.currentDate();
    let proQuery = getQuery()
    proQuery += '&product_type=services'
    const productsRes = fetchProductList(proQuery);
    const serviceResourceGroupRes = fetchGroup(companyId, 'human_resources');

    await Promise.all([
      fetchContacts(proQuery).then(({data}) => {
        if(data) userList.value = data;
      }),
      productsRes.then(({data}) => {
        if (data) productList.value = data;
      }),
      serviceResourceGroupRes.then(({data}) => {
        if (data) serviceResourceGroups.value = data;
      }),
    ]).then(() => {
      loading.value = false;
    }).catch((err) => {
      loading.value = false
    });
})

watch(
    () => props.clientRes,
    (newVal) => {
        if (!newVal || !newVal.id) return;
        formData.value.contact_profile_client_id = newVal.id;

        clientAdditionalOption.value = [{
            id: newVal.id,
            name: newVal.name
        }];
    },
    { immediate: true }
);

watch(
    () => props.serviceResourceRes,
    (newVal) => {
        if (!newVal || !newVal.id) return;

        formData.value.service_resource_id = newVal.id;

        consultantAdditionalOption.value = [{
            id: newVal.id,
            name: newVal.name
        }];
    },
    { immediate: true }
);

watch(
    () => props.referrerRes,
    (newVal) => {
        if (!newVal || !newVal.id) return;
        formData.value.referrer_id = newVal.id;

        referrerAdditionalOption.value = [{
            id: newVal.id,
            name: newVal.name
        }];
    },
    { immediate: true }
);

watch(
    () => formData.value.contact,
    (contact) => {
        if (!contact) return;

        // Patient AsyncSelect
        patient.value = {
            id: contact.id,
            name: contact.name
        };

        formData.value.contact_profile_id = contact.id;
    },
    { immediate: true }
);

watch(patient, () => {
    if (! patient.value) return;

    registrationList.value = [];
    const patientQuery = companyQuery + '&type=patient';
    formData.value.contact_profile_id = patient.value.id;
    const ipdQuery = companyQuery + `&contact_profile_id=${formData.value.contact_profile_id}`;

    fetchSingleContactProfile(patient.value.id, patientQuery).then(res => {
        if (res.data) {
            selectedData.value = res.data;
        }
    })

    fetchIpdRegisterList(ipdQuery).then(res => {
        if(res.data) registrationList.value = res.data
    })
})

watch(productId, async () => {
    if (! productId.value) return;
    const found = !!props.tableItems.find(item => item.product_id === productId.value);

    if (found) {
        showError('Item already selected!');
        focusProductSelect();
        return;
    }

    const productResp = await getProduct({
        id: productId.value,
        company_id: companyId
    })
    if (!productResp.data) {
        showError("Something went wrong please try again");
        focusProductSelect();
        return;
    }
    let selected = productResp.data;
    let product = {
        name: selected.name
    }
    props.tableItems.push({
        id: selected.id,
        product_id: productId.value,
        name: selected.name,
        description: selected.description.unit.description,
        quantity: 1,
        rate: selected.description.sales_price,
        discount: 0,
        vat: selected.description.vat_rate,
        sub_total: 0,
        discount_amount: 0,
        discount_percent: 0,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        product: product
    })

   focusProductSelect();
})

const focusProductSelect = () => {
  productSelect.value.dropdownRef.searchEl.focus()
  productId.value = null;
}

const searchPatientById = async () => {

    if(!patientSerialNo.value) return;
    isLoader.value = true;
    const query = `?company_id=${companyId}`;
    userInfo.value = null
    patient.value = null
    await fetchPatientDetailsBySerial(query, patientSerialNo.value)
        .then((res) => {
            
            if (!res.status) {
                return showError(res.message)
            }

            formData.value.ipd_register_id = null
            
            userInfo.value = res.data
            patient.value = {
                id: userInfo.value.id,
                name: userInfo.value.full_name
            }
        })
    
    isLoader.value = false;
}

const onCreateProfile = (profileInfo) => {
    patient.value = profileInfo;
    patient.value.name = profileInfo.full_name
}

const onCreateContact = (contactInfo) => {
    referrerAdditionalOption.value = [{
        id: contactInfo.id,
        name: contactInfo.full_name
    }];
    formData.value.referrer_id = contactInfo.id;
}

const addNewContact = () => {
    $store.state.isModalOpenOne = true;
}

const addNewConsultant = () => {
    clearResourceModal()
    resourceModalRef.value.toggleModal()
}

const handleResourceSubmit = async () => {
    resourceFormData.value.company_id = companyId;

    try {
      loading.value = true
      let res = await storeServiceResource(resourceFormData.value);
      if (!res.status) {
          showError(res.message)
          return;
      }
      formData.value.service_resource_id = res.data.id;
      consultantAdditionalOption.value = [{
          id: res.data.id,
          name: res.data.name
      }];
      resourceModalRef.value.toggleModal()
      clearResourceModal()
      showSuccess(res.message)
    } catch (err) {
        showError('Something is wrong. Check your connectivity!!')
    } finally {
        loading.value = false
    }
}

const clearResourceModal = () => {
  resourceFormData.value = {
    contact_profile_id: '',
    service_user_id: null,
    name: '',
    service_code: '',
    short_description: '',
    full_description: '',
    resource_type: 'human_resources',
    top_margin: 170,
    bottom_margin: 86,
    left_margin: 25,
    right_margin: 25,
    status: 'active',
    company_id: '',
    head_group_id: null,
    resource_descriptions: [
      {
        product_id: '',
        service_resource_id: '',
        sales_account_head_id: '',
        sales_price: 0,
        cost_of_sales_account_head_id: '',
        cost_of_sales_price: 0,
        service_specification: '',
        service_duration: '',
        is_opd_billing: false,
        is_repeat_weekly: 1,
        schedules: '',
      }
    ]
  }
}

const getQuery = () => {
    let query = '?company_id=' + companyId
    query += '&dropdown=' + true
    return query
}

</script>

<style scoped>
.select label {
    font-size: 14px;
}
.h-40 {
    height: 40px;
    align-items: center;
}
.patient-action {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.mt-8 {
    margin-top: 8px;
}
</style>